import {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { ColumnDef, OnChangeFn, SortingState } from "@tanstack/react-table";
import {
  ArrowLeft,
  FunnelSimple,
  MagnifyingGlass,
  PencilSimple,
  Plus,
} from "@phosphor-icons/react";

import { Breadcrumbs } from "../../../../components/Breadcrumbs";
import type { BreadcrumbItem } from "../../../../components/Breadcrumbs";
import { DataTable } from "../../../../components/table/DataTable";
import { useApiQuery } from "../../../../hooks/useApiQuery";
import { useQueryParamState } from "../../../../hooks/useQueryParamState";
import { useAuth } from "../../../../providers/AuthProvider";
import { ApiError } from "../../../../services/apiClient";
import { PageShell } from "../../../shared/layouts/PageShell";
import { PageHeader } from "../../../shared/ui/layouts/PageHeader";
import { defaultIconProps } from "../../../shared/ui/icons";
import {
  listAdminControlPoints,
  type AdminControlPointListResponse,
  type ControlPoint,
  type ListAdminControlPointsParams,
} from "../../../shared/api/controlPoints";
import {
  listMunicipalities,
  listProvinces,
  listRegions,
  type GeoMunicipality,
  type GeoProvince,
  type GeoRegion,
} from "../../../shared/api/geo";
import { useBreadcrumbBack } from "../../../../components/useBreadcrumbBack";

interface NavigationState {
  refreshed?: boolean;
}

type HasProjectedFilter = "any" | "with" | "without";

type FilterState = {
  regionId: string;
  provinceId: string;
  municipalityId: string;
  srid: string;
  projectedSrid: string;
  hasProjected: HasProjectedFilter;
};

const defaultFilters: FilterState = {
  regionId: "",
  provinceId: "",
  municipalityId: "",
  srid: "",
  projectedSrid: "",
  hasProjected: "any",
};

const sortableFields = new Set(["code", "updated_at", "srid", "region", "province"]);
const pageStackClasses = "flex flex-col gap-8 text-slate-900 dark:text-slate-100";
const toolbarGridClasses =
  "grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4 items-center";
const inputClasses = "pm-input";
const selectClasses = "pm-input";
const errorBannerClasses =
  "rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-900/40 dark:bg-rose-900/20 dark:text-rose-100";
const ghostButtonClasses = "pm-btn pm-btn-ghost";
const primaryButtonClasses = "pm-btn pm-btn-primary";

const parsePositiveInt = (value: string): number | undefined => {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
};

const buildSortingState = (sortParam: string): SortingState => {
  const [field, direction] = sortParam.split(".");
  const column = sortableFields.has(field) ? field : "code";
  return [{ id: column, desc: direction === "desc" }];
};

const buildSortParam = (sorting: SortingState): string => {
  const primary = sorting[0];
  const field = primary?.id && sortableFields.has(primary.id) ? primary.id : "code";
  const direction = primary?.desc ? "desc" : "asc";
  return `${field}.${direction}`;
};

const formatCoordinate = (value?: number | null) => {
  if (!Number.isFinite(value ?? NaN)) {
    return "—";
  }
  return (value as number).toFixed(6);
};

export function AdminTiePointsPage() {
  const { t } = useTranslation();
  const { user, accessToken } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filters, setFilters] = useState<FilterState>(defaultFilters);
  const [regions, setRegions] = useState<GeoRegion[]>([]);
  const [provinces, setProvinces] = useState<GeoProvince[]>([]);
  const [municipalities, setMunicipalities] = useState<GeoMunicipality[]>([]);
  const [geoError, setGeoError] = useState<string | null>(null);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
    }, 250);
    return () => window.clearTimeout(handle);
  }, [searchInput]);

  const [page, setPage] = useQueryParamState<number>({
    key: "page",
    defaultValue: 1,
    parse: (value) => {
      const parsed = Number.parseInt(value, 10);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
    },
    serialize: (value) => value.toString(),
    replace: true,
  });

  const [pageSize, setPageSize] = useQueryParamState<number>({
    key: "limit",
    defaultValue: 25,
    parse: (value) => {
      const parsed = Number.parseInt(value, 10);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
    },
    serialize: (value) => value.toString(),
    replace: true,
  });

  const [sortParam, setSortParam] = useQueryParamState<string>({
    key: "sort",
    defaultValue: "code.asc",
    parse: (value) => (value.includes(".") ? value : null),
    serialize: (value) => value,
    replace: true,
  });

  const sorting = useMemo<SortingState>(() => buildSortingState(sortParam), [sortParam]);

  useEffect(() => {
    if (page !== 1) {
      setPage(1);
    }
  }, [filters, debouncedSearch, page, setPage]);

  const loadRegions = useCallback(async () => {
    if (!accessToken) {
      return;
    }
    try {
      const regionList = await listRegions(accessToken);
      setRegions(regionList);
    } catch (err) {
      setGeoError(
        err instanceof ApiError ? err.message : t("admin.tie_points.filters.geo_error")
      );
    }
  }, [accessToken, t]);

  useEffect(() => {
    void loadRegions();
  }, [loadRegions]);

  useEffect(() => {
    if (!accessToken || !filters.regionId) {
      setProvinces([]);
      return;
    }
    const fetchProvinces = async () => {
      try {
        const provinceList = await listProvinces(accessToken, filters.regionId);
        setProvinces(provinceList);
      } catch (err) {
        setGeoError(
          err instanceof ApiError ? err.message : t("admin.tie_points.filters.geo_error")
        );
      }
    };
    void fetchProvinces();
  }, [accessToken, filters.regionId, t]);

  useEffect(() => {
    if (!accessToken || !filters.provinceId) {
      setMunicipalities([]);
      return;
    }
    const fetchMunicipalities = async () => {
      try {
        const municipalityList = await listMunicipalities(accessToken, filters.provinceId);
        setMunicipalities(municipalityList);
      } catch (err) {
        setGeoError(
          err instanceof ApiError ? err.message : t("admin.tie_points.filters.geo_error")
        );
      }
    };
    void fetchMunicipalities();
  }, [accessToken, filters.provinceId, t]);

  const listParams = useMemo(() => {
    const sridValue = parsePositiveInt(filters.srid);
    const projectedValue = parsePositiveInt(filters.projectedSrid);
    const [sortField, sortDirection] = sortParam.split(".");
    const safeSort = sortableFields.has(sortField) ? sortField : "code";
    const order = sortDirection === "desc" ? "desc" : "asc";
    const safePageSize = pageSize > 0 ? pageSize : 25;

    return {
      limit: safePageSize,
      offset: Math.max(0, (page - 1) * safePageSize),
      query: debouncedSearch || undefined,
      regionId: filters.regionId || undefined,
      provinceId: filters.provinceId || undefined,
      municipalityId: filters.municipalityId || undefined,
      srid: sridValue,
      projectedSrid: projectedValue,
      hasProjected:
        filters.hasProjected === "any"
          ? undefined
          : filters.hasProjected === "with",
      sort: safeSort as ListAdminControlPointsParams["sort"],
      order: order as ListAdminControlPointsParams["order"],
    };
  }, [debouncedSearch, filters, page, pageSize, sortParam]);

  const queryFn = useCallback(
    (token: string) => listAdminControlPoints(token, listParams),
    [listParams]
  );

  const formatError = useCallback(
    (err: unknown) =>
      err instanceof ApiError ? err.message : t("admin.tie_points.load_error"),
    [t]
  );

  const { data, isLoading, error, refresh } =
    useApiQuery<AdminControlPointListResponse>({
      queryFn,
      formatError,
    });

  useEffect(() => {
    const navState = location.state as NavigationState | undefined;
    if (navState?.refreshed) {
      void refresh();
      navigate(location.pathname, { replace: true });
    }
  }, [location, navigate, refresh]);

  const breadcrumbItems = useMemo<BreadcrumbItem[]>(() => {
    return [
      { label: t("admin.title"), to: "/admin" },
      { label: t("admin.tie_points.list_title") },
    ];
  }, [t]);

  const handleBack = useBreadcrumbBack({
    items: breadcrumbItems,
    fallback: { to: "/admin" },
  });

  const handleAdd = () => {
    navigate("/admin/tie-points/new");
  };

  const handleEdit = useCallback(
    (controlPoint: ControlPoint) => {
      navigate(`/admin/tie-points/${controlPoint.id}/edit`, {
        state: { controlPoint },
      });
    },
    [navigate]
  );

  const tableColumns = useMemo<ColumnDef<ControlPoint>[]>(() => {
    return [
      {
        id: "code",
        accessorKey: "code",
        header: t("admin.tie_points.table.code"),
        enableSorting: true,
        cell: ({ row }) => (
          <span className="font-mono font-semibold text-slate-900 dark:text-slate-50">
            {row.original.code}
          </span>
        ),
      },
      {
        id: "name",
        accessorKey: "name",
        header: t("admin.tie_points.table.name"),
        enableSorting: false,
        cell: ({ row }) =>
          row.original.name?.trim() || t("common.table.empty_value"),
      },
      {
        id: "region",
        header: t("admin.tie_points.table.region"),
        enableSorting: true,
        cell: ({ row }) =>
          row.original.region_name ?? t("common.table.empty_value"),
      },
      {
        id: "province",
        header: t("admin.tie_points.table.province"),
        enableSorting: true,
        cell: ({ row }) =>
          row.original.municipality_name ??
          row.original.province_name ??
          t("common.table.empty_value"),
      },
      {
        id: "srid",
        accessorKey: "srid",
        header: t("admin.tie_points.table.srid"),
        enableSorting: true,
        cell: ({ row }) => row.original.srid,
      },
      {
        id: "projected_srid",
        accessorKey: "projected_srid",
        header: t("admin.tie_points.table.projected_srid"),
        enableSorting: false,
        cell: ({ row }) =>
          row.original.projected_srid ?? t("common.table.empty_value"),
      },
      {
        id: "coordinates",
        header: t("admin.tie_points.table.coordinates"),
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex flex-col text-xs text-slate-600 dark:text-slate-300">
            <span className="font-mono">{formatCoordinate(row.original.latitude)}</span>
            <span className="font-mono text-slate-500 dark:text-slate-400">
              {formatCoordinate(row.original.longitude)}
            </span>
          </div>
        ),
      },
      {
        id: "source",
        accessorKey: "source",
        header: t("admin.tie_points.table.source"),
        enableSorting: false,
        cell: ({ row }) =>
          row.original.source?.trim() || t("common.table.empty_value"),
      },
      {
        id: "updated_at",
        accessorKey: "updated_at",
        header: t("admin.tie_points.table.updated"),
        enableSorting: true,
        cell: ({ row }) =>
          row.original.updated_at
            ? new Date(row.original.updated_at).toLocaleString()
            : t("common.table.empty_value"),
      },
      {
        id: "actions",
        header: t("common.table.actions"),
        enableSorting: false,
        cell: ({ row }) => (
          <button
            type="button"
            className="pm-btn pm-btn-surface pm-btn-xs"
            onClick={() => handleEdit(row.original)}
          >
            <PencilSimple {...defaultIconProps} aria-hidden />
            {t("actions.edit")}
          </button>
        ),
      },
    ];
  }, [handleEdit, t]);

  const handleSortingChange = useCallback<OnChangeFn<SortingState>>(
    (updater) => {
      const next =
        typeof updater === "function" ? updater(sorting) : updater ?? [];
      setSortParam(buildSortParam(next));
    },
    [setSortParam, sorting]
  );

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchInput(event.target.value);
  };

  const handleFilterChange =
    (field: keyof FilterState) =>
    (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const value = event.target.value;
      setFilters((prev) => ({ ...prev, [field]: value }));
    };

  const handleRegionChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value;
    setFilters((prev) => ({
      ...prev,
      regionId: value,
      provinceId: "",
      municipalityId: "",
    }));
    setProvinces([]);
    setMunicipalities([]);
  };

  const handleProvinceChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value;
    setFilters((prev) => ({
      ...prev,
      provinceId: value,
      municipalityId: "",
    }));
    setMunicipalities([]);
  };

  const handlePageChange = (nextPageIndex: number) => {
    setPage(nextPageIndex + 1);
  };

  const handlePageSizeChange = (nextPageSize: number) => {
    setPageSize(nextPageSize);
    setPage(1);
  };

  const resetFilters = () => {
    setFilters(defaultFilters);
    setSearchInput("");
    setDebouncedSearch("");
    setPage(1);
  };

  if (!user || !accessToken) {
    return null;
  }

  if (user.system_role !== "admin") {
    return (
      <PageShell className={pageStackClasses} width="wide">
        <Breadcrumbs items={breadcrumbItems} />
        <PageHeader
          title={t("common.access_denied.title")}
          subtitle={t("common.access_denied.message")}
          actions={
            <button
              type="button"
              className={ghostButtonClasses}
              onClick={handleBack}
            >
              <ArrowLeft {...defaultIconProps} aria-hidden />
              {t("actions.back")}
            </button>
          }
        />
        <button
          type="button"
          className={ghostButtonClasses}
          onClick={() => navigate("/dashboard")}
        >
          <ArrowLeft {...defaultIconProps} aria-hidden />
          {t("navigation.to_dashboard")}
        </button>
      </PageShell>
    );
  }

  const controlPoints = data?.control_points ?? [];
  const total = data?.total ?? 0;
  const effectiveLimit = data?.limit || pageSize || 25;
  const effectiveOffset = data?.offset ?? 0;
  const pageIndex = effectiveLimit > 0 ? Math.floor(effectiveOffset / effectiveLimit) : 0;

  return (
    <PageShell className={pageStackClasses} width="wide">
      <Breadcrumbs items={breadcrumbItems} />
      <PageHeader
        title={t("admin.tie_points.list_title")}
        subtitle={t("admin.tie_points.list_subtitle")}
        actions={
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              className={primaryButtonClasses}
              onClick={handleAdd}
            >
              <Plus {...defaultIconProps} aria-hidden />
              {t("admin.tie_points.add_button")}
            </button>
            <button
              type="button"
              className={ghostButtonClasses}
              onClick={handleBack}
            >
              <ArrowLeft {...defaultIconProps} aria-hidden />
              {t("actions.back")}
            </button>
          </div>
        }
      />

      {error ? (
        <div className={errorBannerClasses} role="alert">
          {error}
        </div>
      ) : null}
      {geoError ? (
        <div className={errorBannerClasses} role="alert">
          {geoError}
        </div>
      ) : null}

      <DataTable
        columns={tableColumns}
        data={controlPoints}
        sorting={sorting}
        onSortingChange={handleSortingChange}
        isLoading={isLoading}
        loadingMessage={t("admin.tie_points.loading")}
        emptyMessage={t("admin.tie_points.empty_state")}
        getRowId={(row) => row.id}
        renderToolbar={
          <div className={toolbarGridClasses}>
            <div className="relative flex flex-1 items-center">
              <MagnifyingGlass
                {...defaultIconProps}
                className="pointer-events-none absolute left-3 h-5 w-5 text-slate-400"
                aria-hidden
              />
              <input
                type="search"
                className={`${inputClasses} pl-10`}
                placeholder={t("admin.tie_points.filters.search_placeholder")}
                aria-label={t("admin.tie_points.filters.search_placeholder")}
                value={searchInput}
                onChange={handleSearchChange}
              />
            </div>
            <div className="flex flex-1 flex-col gap-3 sm:flex-row sm:items-center">
              <select
                className={selectClasses}
                value={filters.regionId}
                onChange={handleRegionChange}
                aria-label={t("admin.tie_points.filters.region_label")}
              >
                <option value="">{t("admin.tie_points.filters.region_label")}</option>
                {regions.map((region) => (
                  <option key={region.id} value={region.id}>
                    {region.name}
                  </option>
                ))}
              </select>
              <select
                className={selectClasses}
                value={filters.provinceId}
                onChange={handleProvinceChange}
                aria-label={t("admin.tie_points.filters.province_label")}
                disabled={!filters.regionId}
              >
                <option value="">{t("admin.tie_points.filters.province_label")}</option>
                {provinces.map((province) => (
                  <option key={province.id} value={province.id}>
                    {province.name}
                  </option>
                ))}
              </select>
              <select
                className={selectClasses}
                value={filters.municipalityId}
                onChange={handleFilterChange("municipalityId")}
                aria-label={t("admin.tie_points.filters.municipality_label")}
                disabled={!filters.provinceId}
              >
                <option value="">{t("admin.tie_points.filters.municipality_label")}</option>
                {municipalities.map((municipality) => (
                  <option key={municipality.id} value={municipality.id}>
                    {municipality.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <input
                type="number"
                inputMode="numeric"
                className={inputClasses}
                placeholder={t("admin.tie_points.filters.srid_label")}
                aria-label={t("admin.tie_points.filters.srid_label")}
                value={filters.srid}
                onChange={handleFilterChange("srid")}
              />
              <input
                type="number"
                inputMode="numeric"
                className={inputClasses}
                placeholder={t("admin.tie_points.filters.projected_srid_label")}
                aria-label={t("admin.tie_points.filters.projected_srid_label")}
                value={filters.projectedSrid}
                onChange={handleFilterChange("projectedSrid")}
              />
              <select
                className={selectClasses}
                value={filters.hasProjected}
                onChange={handleFilterChange("hasProjected")}
                aria-label={t("admin.tie_points.filters.has_projected_label")}
              >
                <option value="any">{t("admin.tie_points.filters.has_projected_any")}</option>
                <option value="with">{t("admin.tie_points.filters.has_projected_with")}</option>
                <option value="without">
                  {t("admin.tie_points.filters.has_projected_without")}
                </option>
              </select>
            </div>
            <div className="flex items-center justify-between gap-3">
              <div className="inline-flex items-center gap-2 rounded-xl bg-slate-100 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-600 dark:bg-slate-800 dark:text-slate-200">
                <FunnelSimple {...defaultIconProps} aria-hidden />
                {t("admin.tie_points.filters.label")}
              </div>
              <button
                type="button"
                className="pm-btn pm-btn-tonal pm-btn-sm"
                onClick={resetFilters}
              >
                {t("admin.tie_points.filters.clear")}
              </button>
            </div>
          </div>
        }
        pagination={{
          pageIndex,
          pageSize: effectiveLimit,
          total,
          onPageChange: handlePageChange,
          onPageSizeChange: handlePageSizeChange,
          pageSizeOptions: [25, 50, 100],
        }}
      />
    </PageShell>
  );
}
